import AddIcon from '@mui/icons-material/Add';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RemoveIcon from '@mui/icons-material/Remove';
import { Button, Collapse, Stack, TextField, Tooltip, Typography } from '@mui/material';
import React, { useState } from 'react';

import { getColumnsBySectionTypeAndFilter } from '../../constants/reportTemplateColumnList';
import { SectionType } from '../../validationSchemas/ReportTemplateSchema';

interface ColumnSelectorProps {
  sectionType: SectionType;
  options: any[];
  onChange: Function;
  open?: boolean;
  disabledColumnIds: number[];
}

function ColumnSelector({ sectionType, options, onChange, open = false, disabledColumnIds }: ColumnSelectorProps) {
  const [isOpen, setIsOpen] = useState(open);
  const [isOpenCategory, setIsOpenCategory] = useState({});
  const [isOpenSubcategory, setIsOpenSubcategory] = useState({});
  const [hoveredId, setHoveredId] = useState();

  const [filter, setFilter] = useState('');
  const reportTemplateColumnList = getColumnsBySectionTypeAndFilter(sectionType, filter);

  const addColumn = (column) => {
    if (!options.includes(column.id)) {
      const newColumnIds = [...options, column.id];
      onChange(newColumnIds, column.id);
    }
  };

  const removeColumn = (column) => {
    if (options.includes(column.id)) {
      const newColumnIds = options.filter((columnId) => columnId !== column.id);
      onChange(newColumnIds, column.id);
    }
  };

  const toggleOpenCategory = (category) => {
    const isCurrentlyOpen = isOpenCategory[category];
    const newOpenCategory = { ...isOpenCategory };
    if (isCurrentlyOpen) {
      delete newOpenCategory[category];
    } else {
      newOpenCategory[category] = true;
    }
    setIsOpenCategory(newOpenCategory);
  };

  const toggleOpenSubcategory = (subcategory) => {
    const isCurrentlyOpen = isOpenSubcategory[subcategory];
    const newOpenSubcategory = { ...isOpenSubcategory };
    if (isCurrentlyOpen) {
      delete newOpenSubcategory[subcategory];
    } else {
      newOpenSubcategory[subcategory] = true;
    }
    setIsOpenSubcategory(newOpenSubcategory);
  };

  const style1 = {
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '24px',
    letterSpacing: '0.17px',
  };

  return (
    <Stack spacing={2} pb={10}>
      <Stack direction="row" justifyContent="space-between" px={2}>
        <Typography sx={style1}>Add Columns</Typography>
        <Button variant="text" size="small" onClick={() => setIsOpen(!isOpen)}>
          {isOpen ? 'Hide' : 'Show'}
        </Button>
      </Stack>
      <Collapse in={isOpen} timeout="auto" unmountOnExit={false}>
        <Stack spacing={2}>
          <Stack px={2}>
            <TextField
              label="Search"
              placeholder="Search..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </Stack>
          {Object.entries(reportTemplateColumnList).map(([category, categoryData]: [string, any]) => (
            <Stack spacing={2} key={category}>
              <Stack
                direction="row"
                justifyContent="space-between"
                onClick={() => toggleOpenCategory(category)}
                aria-label={`${isOpenCategory[category] ? 'Collapse' : 'Expand'} Category ${category}`}
                sx={{ cursor: 'pointer' }}
                px={2}
              >
                <Typography>
                  {category} ({categoryData.count})
                </Typography>
                <Stack>{isOpenCategory[category] ? <ExpandLessIcon /> : <ExpandMoreIcon />}</Stack>
              </Stack>
              <Collapse in={isOpenCategory[category]} timeout="auto" unmountOnExit={false}>
                <Stack spacing={2}>
                  {Object.entries(categoryData.subcategories).map(([subcategory, subcategoryData]: [string, any]) => (
                    <Stack key={subcategory} spacing={2}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        onClick={() => toggleOpenSubcategory(subcategory)}
                        aria-label={`${
                          isOpenSubcategory[subcategory] ? 'Collapse' : 'Expand'
                        } Subcategory ${subcategory}`}
                        sx={{ cursor: 'pointer' }}
                        px={2}
                        pl={4}
                      >
                        <Typography>
                          {subcategory} ({subcategoryData.columns.length})
                        </Typography>
                        <Stack>{isOpenSubcategory[subcategory] ? <ExpandLessIcon /> : <ExpandMoreIcon />}</Stack>
                      </Stack>
                      <Collapse in={isOpenSubcategory[subcategory]} timeout="auto" unmountOnExit={false}>
                        <Stack>
                          {subcategoryData.columns.map((column) => (
                            <Tooltip
                              key={column.id}
                              title={
                                (subcategoryData.enabled &&
                                  (options.includes(column.id) ? 'Remove Column' : column.tooltip || 'Add Column')) ||
                                `Only available in ${subcategoryData.sectionTypes.join(', ')} section types.`
                              }
                              placement="left"
                            >
                              <Stack
                                key={column.id}
                                direction="row"
                                justifyContent="space-between"
                                onClick={() => {
                                  if (subcategoryData.enabled) {
                                    if (options.includes(column.id)) {
                                      removeColumn(column);
                                    } else if (!disabledColumnIds.includes(column.id)) addColumn(column);
                                  }
                                }}
                                aria-label={`${options.includes(column.id) ? 'Remove' : 'Add'} Column ${column.label}`}
                                sx={{ cursor: 'pointer', bgcolor: hoveredId === column.id && 'rgba(0, 0, 0, 0.04)' }}
                                onMouseOver={() => setHoveredId(column.id)}
                                onMouseOut={() => setHoveredId(undefined)}
                                px={2}
                                py={1}
                                pl={4}
                              >
                                <Stack>
                                  <Typography color="grey">{column.label}</Typography>
                                </Stack>
                                <Stack>
                                  {!options.includes(column.id) && (
                                    <AddIcon
                                      color={
                                        subcategoryData.enabled && !disabledColumnIds.includes(column.id)
                                          ? 'success'
                                          : 'disabled'
                                      }
                                    />
                                  )}
                                  {options.includes(column.id) && (
                                    <RemoveIcon color={subcategoryData.enabled ? 'success' : 'disabled'} />
                                  )}
                                </Stack>
                              </Stack>
                            </Tooltip>
                          ))}
                        </Stack>
                      </Collapse>
                    </Stack>
                  ))}
                </Stack>
              </Collapse>
            </Stack>
          ))}
        </Stack>
      </Collapse>
    </Stack>
  );
}

export default ColumnSelector;
